import React, { useEffect, useMemo, useState } from "react";
import InputMask from "react-input-mask";
import { Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { listarRevendasDropdown, listarPerfisRevenda } from "../services/api";
import { useFeedbackAlert } from "./common/useFeedbackAlert";
import PermissoesUsuario, { flagsPadrao } from "./PermissoesUsuario";

const ensureArray = (v) => (Array.isArray(v) ? v : []);

const pickList = (resp) => {
  const d = resp?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(d?.Items)) return d.Items;
  return [];
};

const CadastroRevendaUsuario = ({
  token,
  initialData = {},
  onSubmit,
  isEdit = false,
  revendaIdFromQuery = "",
}) => {
  const { showError, showSuccess, showWarning, FeedbackAlert } = useFeedbackAlert();
  const navigate = useNavigate();

  const [revendas, setRevendas] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [loadingRevendas, setLoadingRevendas] = useState(false);
  const [permissoes, setPermissoes] = useState(flagsPadrao("revenda"));

  const [usuario, setUsuario] = useState({
    revendaId: revendaIdFromQuery
      ? Number(revendaIdFromQuery)
      : Number(initialData?.revendaId ?? 0),
    nome: "",
    telefone: "",
    email: "",
    senha: "",
    ativo: true,
    perfilId: 1,
  });

  const fetchRevendas = async () => {
    setLoadingRevendas(true);
    try {
      const r = await listarRevendasDropdown(token);
      const list = pickList(r);

      // remove MASTER/0 e garante revenda real
      const clean = ensureArray(list).filter((x) => Number(x?.id) > 0);

      setRevendas(clean);
    } catch (e) {
      console.error("Erro ao listar revendas:", e);
      setRevendas([]);
    } finally {
      setLoadingRevendas(false);
    }
  };

  useEffect(() => {
    fetchRevendas();
    listarPerfisRevenda(token)
      .then((res) => setPerfis(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPerfis([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const hasInitial = initialData && Object.keys(initialData).length > 0;

    if (hasInitial) {
      setUsuario({
        revendaId: Number(
          initialData.revendaId ??
            (revendaIdFromQuery ? Number(revendaIdFromQuery) : 0)
        ),
        nome: initialData.nome || "",
        telefone: initialData.telefone || "",
        email: initialData.email || "",
        senha: "",
        ativo: initialData.ativo ?? true,
        perfilId: initialData.perfilId || initialData.PerfilId || 1,
      });
      const salvas = initialData.permissoes || initialData.Permissoes;
      setPermissoes(Array.isArray(salvas) && salvas.length ? salvas : flagsPadrao("revenda"));
      return;
    }

    // novo: garante revendaId do query (se veio)
    if (revendaIdFromQuery) {
      setUsuario((u) => ({ ...u, revendaId: Number(revendaIdFromQuery) }));
    }
  }, [initialData, revendaIdFromQuery]);

  // novo: se NÃO veio query e só existe 1 revenda, já seleciona
  useEffect(() => {
    if (isEdit) return;
    if (revendaIdFromQuery) return;

    const base = ensureArray(revendas);
    if (!base.length) return;

    setUsuario((u) => ({
      ...u,
      revendaId: Number(u.revendaId) > 0 ? Number(u.revendaId) : Number(base[0].id),
    }));
  }, [revendas, isEdit, revendaIdFromQuery]);

  const revendaLabel = useMemo(() => {
    const base = ensureArray(revendas);
    const r = base.find((x) => Number(x?.id) === Number(usuario.revendaId));
    return r?.nomeFantasia || r?.razaoSocial || "";
  }, [revendas, usuario.revendaId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // garante Number no revendaId
    if (name === "revendaId") {
      setUsuario((prev) => ({ ...prev, revendaId: Number(value) || 0 }));
      return;
    }

    if (name === "perfilId") {
      setUsuario((prev) => ({ ...prev, perfilId: Number(value) || 1 }));
      return;
    }

    setUsuario((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      RevendaId: Number(usuario.revendaId),
      Nome: usuario.nome,
      Telefone: usuario.telefone,
      Email: usuario.email,
      Ativo: Boolean(usuario.ativo),
      Senha: usuario.senha,
      PerfilId: Number(usuario.perfilId) || 1,
      Permissoes: permissoes,
    };

    if (!payload.RevendaId || payload.RevendaId <= 0) {
      showWarning("Selecione uma revenda válida.");
      return;
    }

    if (!isEdit && !payload.Senha) {
      showWarning("Senha é obrigatória no cadastro.");
      return;
    }

    try {
      await onSubmit(payload, token);
      showSuccess(
        isEdit ? "Usuário atualizado com sucesso!" : "Usuário cadastrado com sucesso!",
        () => navigate(`/revendas/usuarios?revendaId=${payload.RevendaId}`)
      );
    } catch (error) {
      showError(error, "Erro ao salvar usuário.");
      console.error(error);
    }
  };

  const selectDisabled = !!revendaIdFromQuery || loadingRevendas || isEdit;

  return (
    <>
      <FeedbackAlert />
      <div className="max-w-3xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
            {isEdit ? "Editar Usuário da Revenda" : "Cadastro de Usuário da Revenda"}
          </h2>
          <p className="text-sm text-gray-500 dark:text-slate-300/80">
            {isEdit ? "Altere os dados do usuário." : "Cadastre um novo usuário para a revenda."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 gap-4">
              {/* Revenda combobox */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Revenda
                </label>

                <select
                  name="revendaId"
                  value={String(usuario.revendaId || "")}
                  onChange={handleChange}
                  disabled={selectDisabled}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    disabled:opacity-60 disabled:cursor-not-allowed
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  required
                >
                  <option value="">
                    {loadingRevendas ? "Carregando revendas..." : "Selecione..."}
                  </option>

                  {ensureArray(revendas).map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.nomeFantasia || r.razaoSocial || `Revenda ${r.id}`}
                    </option>
                  ))}
                </select>

                {!!revendaIdFromQuery && (
                  <p className="text-xs text-gray-500 dark:text-slate-300/70 mt-1">
                    Veio filtrado pela revenda selecionada.
                  </p>
                )}

                {!revendaIdFromQuery && Number(usuario.revendaId) > 0 && revendaLabel ? (
                  <p className="text-xs text-gray-500 dark:text-slate-300/70 mt-1">
                    Selecionada: <b>{revendaLabel}</b>
                  </p>
                ) : null}

                {!loadingRevendas && !ensureArray(revendas).length && !isEdit ? (
                  <p className="text-xs text-red-600 mt-1">
                    Nenhuma revenda encontrada. Cadastre uma revenda antes de criar usuário.
                  </p>
                ) : null}
              </div>

              {/* ativo */}
              <div className="flex items-center gap-3">
                <input
                  id="ativo"
                  type="checkbox"
                  name="ativo"
                  checked={!!usuario.ativo}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label
                  htmlFor="ativo"
                  className="text-sm font-medium text-gray-700 dark:text-slate-200"
                >
                  Usuário ativo
                </label>
              </div>
            </div>
          </div>

          {/* dados */}
          <div className="zen-card rounded-2xl p-4 dark:bg-white/5 dark:border-white/10">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Nome
                </label>
                <input
                  type="text"
                  name="nome"
                  value={usuario.nome}
                  onChange={handleChange}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Telefone
                </label>
                <InputMask
                  mask="(99) 99999-9999"
                  type="text"
                  name="telefone"
                  value={usuario.telefone}
                  onChange={handleChange}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={usuario.email}
                  onChange={handleChange}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  Perfil
                </label>
                <select
                  name="perfilId"
                  value={usuario.perfilId}
                  onChange={handleChange}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  required
                >
                  {perfis.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                  {isEdit ? "Nova Senha (deixe em branco para não alterar)" : "Senha"}
                </label>
                <input
                  type="password"
                  name="senha"
                  value={usuario.senha}
                  onChange={handleChange}
                  className="
                    w-full px-3 py-2.5 rounded-xl border outline-none transition
                    zen-card border-gray-300 text-gray-900
                    focus:ring-2 focus:ring-blue-500/60
                    dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                    dark:focus:ring-blue-400/40
                  "
                  required={!isEdit}
                />
                {isEdit ? (
                  <p className="text-xs text-gray-500 dark:text-slate-300/70 mt-1">
                    Se ficar em branco, o backend deve manter a senha atual.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          <PermissoesUsuario audiencia="revenda" value={permissoes} onChange={setPermissoes} />

          {/* actions */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="
                inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
              "
            >
              <Save size={18} />
              {isEdit ? "Salvar Alterações" : "Cadastrar Usuário"}
            </button>
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default CadastroRevendaUsuario;
